import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Calendar, Phone, MessageCircle, MapPin, Clock, ArrowRight, Sparkles, Mail } from 'lucide-react';

const ConsultationModal = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  
  const isEn = language === 'en';
  
  const consultationOptions = [
    {
      id: 'vor-ort',
      icon: MapPin,
      title: isEn ? 'On-Site Consultation' : 'Beratung vor Ort',
      subtitle: isEn ? 'In person at our clinic' : 'Persönlich in unserer Klinik',
      features: isEn 
        ? ['Personal analysis', 'Clinic tour', 'Treatment plan']
        : ['Persönliche Analyse', 'Klinikbesichtigung', 'Behandlungsplan'],
      badge: isEn ? 'Recommended' : 'Empfohlen',
      href: 'https://cal.com/dionhairclinic/beratung-vor-ort',
      color: 'from-[#7BA7C2] to-[#5A8BA6]',
      conversionType: 'booking',
      isExternal: true,
    },
    {
      id: 'telefonisch',
      icon: Phone,
      title: isEn ? 'Phone Consultation' : 'Telefonische Beratung',
      subtitle: isEn ? 'Convenient from home' : 'Bequem von zu Hause',
      features: isEn
        ? ['Flexible scheduling', 'Free & non-binding']
        : ['Flexible Terminwahl', 'Kostenlos & unverbindlich'],
      badge: null,
      href: 'https://cal.com/dionhairclinic/beratung-telefonisch',
      color: 'from-[#5A8BA6] to-[#7BA7C2]',
      conversionType: 'booking',
      isExternal: true,
    },
    {
      id: 'whatsapp',
      icon: MessageCircle,
      title: 'WhatsApp',
      subtitle: isEn ? 'Quick & easy' : 'Schnell & unkompliziert',
      features: isEn
        ? ['Instant message', 'Quick response']
        : ['Sofortige Nachricht', 'Schnelle Antwort'],
      badge: isEn ? 'Fastest' : 'Schnellste',
      href: 'https://wa.me/491722888879',
      color: 'from-[#25D366] to-[#128C7E]',
      conversionType: 'whatsapp',
      isExternal: true,
    },
    {
      id: 'kontaktformular',
      icon: Mail,
      title: isEn ? 'Contact Form' : 'Kontaktformular',
      subtitle: isEn ? 'Write us a message' : 'Schreiben Sie uns eine Nachricht',
      features: isEn
        ? ['Detailed inquiry', 'We respond quickly']
        : ['Ausführliche Anfrage', 'Wir antworten schnell'],
      badge: null,
      href: '#kontakt',
      color: 'from-[#6B7280] to-[#4B5563]',
      conversionType: 'contact',
      isExternal: false,
    },
  ];

  const handleOptionClick = (option) => {
    // Track Google Ads conversion based on type
    if (option.conversionType === 'whatsapp' && typeof window.gtag_report_whatsapp === 'function') {
      window.gtag_report_whatsapp(option.href);
    } else if (option.conversionType === 'booking' && typeof window.gtag_report_booking === 'function') {
      window.gtag_report_booking(option.href);
    }
    
    // Handle internal vs external links
    if (option.isExternal) {
      window.open(option.href, '_blank');
    } else {
      // Scroll to contact form
      const element = document.querySelector(option.href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-0 transition-colors duration-300 ${
          isDark ? 'bg-[#0f0f0f] border-white/10' : 'bg-white border-gray-200'
        }`}
        data-testid="consultation-modal"
      >
        {/* Header - Kompakter auf Mobile */}
        <div className={`relative p-4 sm:p-6 pb-3 sm:pb-4 border-b overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
          {/* Background Glow - nur auf Desktop */}
          <div className={`hidden sm:block absolute top-0 right-0 w-64 h-64 rounded-full blur-[100px] ${isDark ? 'bg-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10'}`} />
          
          <DialogHeader className="relative">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] flex items-center justify-center shadow-lg shadow-[#7BA7C2]/25">
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <DialogTitle className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {isEn ? 'Free Consultation' : 'Kostenlose Beratung'}
                </DialogTitle>
                <DialogDescription className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isEn ? 'Choose your preferred option' : 'Wählen Sie Ihre bevorzugte Option'}
                </DialogDescription>
              </div>
            </div>
            
            {/* Trust Badge - Kompakter auf Mobile */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <div className={`flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${
                isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20 text-[#5A8BA6]'
              }`}>
                <Sparkles className="w-3 h-3" />
                <span className="font-medium">{isEn ? '100% free' : '100% kostenlos'}</span>
              </div>
              <div className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-full border text-xs ${
                isDark ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'
              }`}>
                <Clock className="w-3 h-3" />
                <span>{isEn ? 'Appointments this week' : 'Termine diese Woche'}</span>
              </div>
            </div>
          </DialogHeader>
        </div>

        {/* Options - Kompakter auf Mobile */}
        <div className="p-3 sm:p-5 space-y-2 sm:space-y-3">
          {consultationOptions.map((option, index) => (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option)}
              className={`group w-full text-left relative overflow-hidden rounded-xl sm:rounded-2xl border transition-all duration-300 ${
                isDark 
                  ? 'border-white/10 hover:border-white/20 bg-white/[0.02] hover:bg-white/[0.04] active:bg-white/[0.06]'
                  : 'border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-white active:bg-gray-100'
              }`}
              data-testid={`consultation-option-${option.id}`}
            >
              {/* Hover Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
              
              <div className="relative p-3 sm:p-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  {/* Icon */}
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300`}>
                    <option.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className={`text-sm sm:text-base font-semibold transition-colors duration-300 ${
                        isDark 
                          ? 'text-white group-hover:text-[#7BA7C2]'
                          : 'text-gray-900 group-hover:text-[#5A8BA6]'
                      }`}>
                        {option.title}
                      </h3>
                      {option.badge && (
                        <span className={`px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                          option.id === 'whatsapp' 
                            ? 'bg-[#25D366]/20 text-[#25D366]' 
                            : isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/20 text-[#5A8BA6]'
                        }`}>
                          {option.badge}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{option.subtitle}</p>
                    
                    {/* Features - nur auf Desktop vollständig */}
                    <div className="hidden sm:flex flex-wrap gap-1.5 mt-2">
                      {option.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className={`px-2 py-0.5 rounded text-xs ${
                            isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Arrow */}
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isDark 
                        ? 'bg-white/5 group-hover:bg-white/10'
                        : 'bg-gray-100 group-hover:bg-gray-200'
                    }`}>
                      <ArrowRight className={`w-4 h-4 group-hover:translate-x-0.5 transition-all duration-300 ${
                        isDark ? 'text-gray-400 group-hover:text-white' : 'text-gray-400 group-hover:text-gray-900'
                      }`} />
                    </div>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer - Kompakter auf Mobile */}
        <div className={`p-3 sm:p-4 pt-2 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
          <p className={`text-center text-xs sm:text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            <a 
              href="tel:+4921612963017" 
              className={`transition-colors ${isDark ? 'text-[#7BA7C2] hover:text-[#9BBFD9]' : 'text-[#5A8BA6] hover:text-[#7BA7C2]'}`}
            >
              +49 2161 2963017
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationModal;
